import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { FirebaseService } from '../../../shared/storage/FirebaseService';
import ParentSignupForm from './ParentSignupForm';
import { ParentSignupFormData } from '../../../types';

const ParentCreateAccount: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleParentSignup = async (formData: ParentSignupFormData) => {
    setLoading(true);
    setError(null);

    try {
      const firebaseUser = await FirebaseService.createUser(
        formData.parentEmail.toLowerCase(),
        formData.password
      );

      const uid = firebaseUser.uid;
      const now = new Date().toISOString();

      const userRef = doc(db, 'users', uid);

      const tosConsent = {
        accepted: true,
        version: '2025-01-08',
        url: '/legal/terms',
        acceptedAt: now
      };

      const photoReleaseConsent = {
        value: formData.photoRelease,
        version: '2025-01-08',
        url: '/legal/photo-release',
        updatedAt: now
      };
      const homeLocation = formData.students[0];

      await setDoc(userRef, {
        email: formData.parentEmail.toLowerCase(),
        displayName: `${formData.parentFirstName.trim()} ${formData.parentLastName.trim()}`,
        locationId: homeLocation?.locationId || null,
        locationSlug: homeLocation?.locationSlug || null,
        partnerId: homeLocation?.partnerId || null,
        legacySchoolId: homeLocation?.legacySchoolId || null,
        role: 'parent',
        status: 'active',
        studentIds: [],
        muses: [],
        selectedMuse: null,
        museCash: 0,
        favoriteSquadIds: [],
        phones: formData.parentPhone ? [formData.parentPhone] : [],
        consents: { tos: tosConsent, photoRelease: photoReleaseConsent },
        studentInfo: formData.students.map(s => ({
          firstName: s.firstName.trim(),
          lastName: s.lastName.trim(),
          locationId: s.locationId,
          locationSlug: s.locationSlug || null,
          partnerId: s.partnerId || null,
          legacySchoolId: s.legacySchoolId || null,
        })),
        inviteToken: formData.inviteToken || null,
        createdAt: serverTimestamp(),
        signupTimestamp: now,
        userAgent: navigator.userAgent
      }, { merge: true });

      const written = await getDoc(userRef);
      if (!written.exists()) throw new Error('User profile was not created in Firestore. Please try again.');

      setSuccess(true);
      setTimeout(() => {
        navigate('/', { state: { message: 'Account created successfully! Please sign in with your email and password.' } });
      }, 3000);
    } catch (err: any) {
      console.error('Parent signup error:', err);
      let errorMessage = 'An unexpected error occurred';
      if (err.code === 'auth/email-already-in-use') errorMessage = 'An account with this email already exists. Please sign in or use a different email.';
      else if (err.code === 'auth/weak-password') errorMessage = 'Password is too weak. Please choose a stronger password.';
      else if (err.code === 'auth/invalid-email') errorMessage = 'Please enter a valid email address.';
      else if (err.message) errorMessage = err.message;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="parent-signup-container">
        <div className="parent-signup-card">
          <div className="parent-signup-header">
            <h1>🎉 Account Created!</h1>
            <p>Welcome to the Overture parent community</p>
          </div>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div className="success-message">
              <h3>Your parent account has been created successfully!</h3>
              <p>We've sent a verification email to your address. Please click the link in the email to verify your account before signing in.</p>
              <p><strong>Check your email to verify your account before signing in.</strong></p>
            </div>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '1.5rem' }}>You will be redirected to the login page in a few seconds...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ParentSignupForm onSubmit={handleParentSignup} loading={loading} error={error} />
  );
};

export default ParentCreateAccount;


